import {
  Box,
  Divider,
  Flex,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
  useMantineTheme,
} from "@mantine/core";
import { IconBolt, IconClock, IconShield } from "@tabler/icons-react";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";

/**
 * Esqueleto de carregamento da configuração de Follow-ups.
 *
 * Títulos de seção, legendas fixas e ícones são renderizados como conteúdo real
 * (estático); os skeletons cobrem apenas os controles dependentes de dados
 * (switch, janelas de envio, prompt e proteções) — espelhando `FollowUpsConfig`.
 */
export function FollowUpsPageSkeleton() {
  const theme = useMantineTheme();
  const { isMobileScreen } = useMobileScreen();

  return (
    <Flex
      direction="column"
      mih="100vh"
      style={{ scrollbarGutter: "stable" }}
      bg={theme.colors["background_3"][0]}>
      <Box
        maw="1200px"
        mx="auto"
        w="100%">
        <Stack
          gap="xl"
          p={isMobileScreen ? "20px" : "32px 48px"}
          w="100%">
          {/* Cabeçalho (conteúdo estático). */}
          <Box>
            <Group
              gap="xs"
              mb={2}>
              <Title
                order={1}
                fz={rem(isMobileScreen ? 28 : 32)}
                ff="heading"
                fw={700}>
                Follow-ups automáticos
              </Title>
              <Skeleton
                height={rem(22)}
                width={rem(64)}
                radius="sm"
              />
            </Group>
            <Text
              fz={14}
              c="dimmed">
              Reaqueça conversas que esfriaram: seu agente envia até 3 lembretes
              para clientes que pararam de responder.
            </Text>
          </Box>

          {/* Master switch — texto estático + skeleton do controle. */}
          <Box
            style={{
              background: "var(--mantine-color-background_3-0)",
              border: "1px solid var(--mantine-color-default-border)",
              borderRadius: "var(--mantine-radius-md)",
              padding: "var(--mantine-spacing-md)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--mantine-spacing-md)",
            }}>
            <Stack gap={2}>
              <Text
                fz={15}
                fw={700}>
                Ativar follow-ups nesta conta
              </Text>
              <Text
                fz={12}
                c="dimmed">
                Quando ligado, o agente envia até 3 mensagens automáticas em
                conversas que esfriaram.
              </Text>
            </Stack>
            <Skeleton
              height={rem(24)}
              width={rem(44)}
              radius="xl"
            />
          </Box>

          {/* Janelas de envio. */}
          <Stack gap="xs">
            <Group
              gap="xs"
              justify="space-between">
              <Group
                gap="xs"
                align="center">
                <IconClock
                  size={16}
                  stroke={1.75}
                />
                <Title
                  order={3}
                  fz={15}
                  ff="heading">
                  Janelas de envio
                </Title>
              </Group>
              <Text
                fz={12}
                c="dimmed">
                Minutos após a última mensagem do cliente
              </Text>
            </Group>
            <Flex
              gap="sm"
              wrap="wrap">
              {Array.from({ length: 3 }).map((_, index) => (
                <Box
                  key={index}
                  style={{
                    flex: "1 1 180px",
                    minWidth: 0,
                    background: "var(--mantine-color-background_3-0)",
                    border: "1px solid var(--mantine-color-default-border)",
                    borderRadius: "var(--mantine-radius-md)",
                    padding: "var(--mantine-spacing-md)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--mantine-spacing-sm)",
                  }}>
                  <Group
                    gap="xs"
                    wrap="nowrap">
                    <Skeleton
                      height={rem(24)}
                      width={rem(24)}
                      radius="sm"
                    />
                    <Skeleton
                      height={rem(13)}
                      width="60%"
                      radius="xl"
                    />
                  </Group>
                  <Skeleton
                    height={rem(36)}
                    radius="sm"
                  />
                  <Skeleton
                    height={rem(11)}
                    width="80%"
                    radius="xl"
                  />
                </Box>
              ))}
            </Flex>
          </Stack>

          {/* Instruções do Follow Up. */}
          <Stack gap="xs">
            <Group
              gap="xs"
              justify="space-between">
              <Group
                gap="xs"
                align="center">
                <IconBolt
                  size={16}
                  stroke={1.75}
                />
                <Title
                  order={3}
                  fz={15}
                  ff="heading">
                  Instruções do Follow Up
                </Title>
              </Group>
              <Text
                fz={12}
                c="dimmed">
                A redação final é gerada pela IA
              </Text>
            </Group>
            <Skeleton
              height={rem(200)}
              radius="sm"
            />
          </Stack>

          <Divider />

          {/* Proteções automáticas. */}
          <Stack gap="xs">
            <Group
              gap="xs"
              align="center">
              <IconShield
                size={16}
                stroke={1.75}
              />
              <Title
                order={3}
                fz={15}
                ff="heading">
                Proteções automáticas
              </Title>
            </Group>
            <Flex
              gap="sm"
              wrap="wrap">
              {Array.from({ length: 3 }).map((_, index) => (
                <Box
                  key={index}
                  style={{
                    flex: 1,
                    minWidth: rem(280),
                    background: "var(--mantine-color-background_1-0)",
                    borderRadius: "var(--mantine-radius-md)",
                    padding: "var(--mantine-spacing-sm)",
                    display: "flex",
                    gap: "var(--mantine-spacing-sm)",
                    alignItems: "flex-start",
                  }}>
                  <Skeleton
                    height={rem(16)}
                    width={rem(16)}
                    radius="sm"
                    mt={2}
                  />
                  <Stack
                    gap={rem(6)}
                    style={{ flex: 1 }}>
                    <Skeleton
                      height={rem(12)}
                      width="55%"
                      radius="xl"
                    />
                    <Skeleton
                      height={rem(10)}
                      radius="xl"
                    />
                    <Skeleton
                      height={rem(10)}
                      width="85%"
                      radius="xl"
                    />
                  </Stack>
                </Box>
              ))}
            </Flex>
          </Stack>
        </Stack>
      </Box>
    </Flex>
  );
}

export default FollowUpsPageSkeleton;
